// Abgelehnten Brief korrigieren: aus dem gespeicherten Entwurf (oder – bei älteren Briefen – aus Empfänger,
// Betreff und Quelldokument) den Dialog „Per Post senden“ wieder vorbelegen.
import { supabase } from '../supabase/client';
import { ladeDateiBlob } from '../dokumente/aktenschrankApi';
import type { EmpfaengerTreffer } from '../dokumente/briefApi';
import type { BriefEntwurf, BriefOptionen } from './epost';
import { mimeAusName } from './emailZuBrief';
import type { Empfaenger } from '../../supabase/functions/epost-brief/logik';

export interface KorrekturAnlage { name: string; mime: string; quelle: { drive?: string; storage?: { bucket: string; path: string } }; laden: () => Promise<Blob> }
export interface Korrektur {
  treffer: EmpfaengerTreffer | null; anschrift: string; betreff: string; text: string; mitAnschreiben: boolean;
  anlagen: KorrekturAnlage[]; optionen?: BriefOptionen; objektId: string | null; hinweis: string | null;
}

export interface KorrekturQuelle {
  id: string; betreff: string | null; empfaenger: Empfaenger; empfaenger_typ: string | null; empfaenger_id: string | null;
  bezug_typ: string | null; bezug_id: string | null; entwurf?: BriefEntwurf | null; objekt?: { id: string } | null;
}

/** Anschrift-Text aus den gespeicherten Empfängerfeldern. */
export const anschriftAusEmpfaenger = (e: Empfaenger) =>
  [...(e?.zeilen ?? []), `${e?.plz ?? ''} ${e?.ort ?? ''}`.trim(), ...(e?.land ? [e.land] : [])].filter(Boolean).join('\n');

export function ladeAnlage(a: { name: string; mime: string; drive?: string; storage?: { bucket: string; path: string } }): KorrekturAnlage {
  const quelle = { drive: a.drive, storage: a.storage };
  return {
    name: a.name, mime: a.mime, quelle,
    laden: async () => {
      if (a.drive) return ladeDateiBlob(a.drive, a.mime || 'application/pdf');
      if (a.storage) {
        const { data, error } = await supabase.storage.from(a.storage.bucket).download(a.storage.path);
        if (error || !data) throw new Error(`Anlage nicht mehr da: ${a.name}`);
        return data;
      }
      throw new Error(`Anlage ohne Quelle: ${a.name}`);
    },
  };
}

export async function ladeKorrektur(b: KorrekturQuelle): Promise<Korrektur> {
  const art = b.empfaenger_typ === 'mieter' || b.empfaenger_typ === 'eigentuemer' || b.empfaenger_typ === 'kontakt' ? b.empfaenger_typ : null;
  const treffer: EmpfaengerTreffer | null = art && b.empfaenger_id ? { art, id: b.empfaenger_id, name: b.empfaenger?.zeilen?.[0] ?? '', sub: '' } : null;
  const objektId = b.objekt?.id ?? null;
  if (b.entwurf) {
    const e = b.entwurf;
    return { treffer, anschrift: e.anschrift, betreff: e.betreff, text: e.text, mitAnschreiben: e.mitAnschreiben, optionen: e.optionen,
      anlagen: e.anlagen.map(ladeAnlage), objektId, hinweis: null };
  }
  // Ältere Briefe ohne Entwurf: Quelldokument aus der Akte als Anlage, Anschreiben neu schreiben
  const anlagen: KorrekturAnlage[] = [];
  if (b.bezug_typ === 'dokument' && b.bezug_id) {
    const { data } = await supabase.from('dokumente').select('gdrive_file_id, datei_name, datei_typ').eq('id', b.bezug_id).maybeSingle();
    if (data?.gdrive_file_id) anlagen.push(ladeAnlage({ name: data.datei_name ?? 'Dokument.pdf', mime: mimeAusName(data.datei_name ?? `x.${data.datei_typ ?? 'pdf'}`), drive: data.gdrive_file_id }));
  }
  return {
    treffer, anschrift: anschriftAusEmpfaenger(b.empfaenger), betreff: b.betreff ?? '', text: '', mitAnschreiben: true, anlagen, objektId,
    hinweis: 'Dieser Brief wurde vor der Korrektur-Funktion verschickt: das Anschreiben bitte neu schreiben bzw. eine Vorlage wählen.',
  };
}
